/* ReservedStockOverrideModal
 *
 * Pops up when a cashier tries to add/increase qty of a product that would
 * dip into the qty reserved for another customer. The cashier can:
 *   • Cancel — leaves the cart unchanged
 *   • Override — enters a 4-digit PIN; on success the action is allowed
 *     and the override is logged to db.cashier_overrides for audit.
 *
 * Usage: parent renders <ReservedStockOverrideModal payload={...} onResolve={...} />
 * `payload` shape:
 *   { product, requestedQty, currentStock, reservedQty, reservedFor, reservationNote, action: fn }
 * `onResolve(approved: bool)` is called when the modal closes.
 */
import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Lock, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReservedStockOverrideModal({ payload, onResolve }) {
  const open = Boolean(payload);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!payload) return null;
  const {
    productName,
    requestedQty,
    availableUnreserved,
    reservedQty,
    reservedFor,
    reservationNote,
  } = payload;

  const close = (approved, approverName = null) => {
    setPin("");
    setError("");
    setBusy(false);
    onResolve(approved, approverName);
  };

  const submitOverride = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!pin || pin.length < 4) {
      setError("Enter a 4-digit PIN");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/auth/verify-pin-override`, {
        pin,
        reason: "sell_reserved_stock",
        context: {
          product_name: productName,
          requested_qty: requestedQty,
          available_unreserved: availableUnreserved,
          reserved_qty: reservedQty,
          reserved_for: reservedFor || null,
        },
      });
      toast.success(`Override approved by ${res.data.user_name}`);
      close(true, res.data.user_name);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid PIN";
      setError(msg);
      setBusy(false);
      setPin("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close(false)}>
      <DialogContent className="max-w-md" data-testid="reserved-stock-override-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5" />
            Reserved Stock Warning
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-1.5">
            <div className="font-semibold text-amber-900">{productName}</div>
            <div className="text-amber-800">
              You are trying to sell <strong>{requestedQty}</strong> unit{requestedQty === 1 ? "" : "s"},
              but only <strong>{availableUnreserved}</strong> {availableUnreserved === 1 ? "is" : "are"} available
              for general sale.
            </div>
            <div className="text-amber-800">
              <strong>{reservedQty}</strong> unit{reservedQty === 1 ? "" : "s"}{" "}
              {reservedQty === 1 ? "is" : "are"} reserved
              {reservedFor ? <> for <strong>{reservedFor}</strong></> : null}
              {reservationNote ? <> — <em>{reservationNote}</em></> : null}.
            </div>
          </div>

          <form onSubmit={submitOverride} className="space-y-2">
            <Label htmlFor="override-pin" className="text-sm flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" />
              Enter PIN to override and sell anyway
            </Label>
            <Input
              id="override-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              maxLength={6}
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, "")); setError(""); }}
              placeholder="••••"
              className="text-center text-2xl tracking-widest h-12"
              data-testid="override-pin-input"
            />
            {error && (
              <p className="text-sm text-red-600 text-center" data-testid="override-error">
                {error}
              </p>
            )}
          </form>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => close(false)}
            disabled={busy}
            data-testid="override-cancel-btn"
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={submitOverride}
            disabled={busy || pin.length < 4}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="override-approve-btn"
          >
            <Lock className="h-4 w-4 mr-1" />
            {busy ? "Verifying…" : "Override & Sell"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
